import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import AuthShell from '../../components/ui/AuthShell';
import { ROUTES } from '../../config/routes';
import { APIServices } from '../../services/axiosCommon';

const RegisterSchema = Yup.object({
  username: Yup.string()
    .matches(/^[a-zA-Z0-9_-]+$/, 'Letters, numbers, underscore, dash only')
    .min(3)
    .max(32)
    .required('Username is required'),
  email: Yup.string().email().required('Email is required'),
  password: Yup.string().min(8).max(128).required('Password is required'),
  firstname: Yup.string().max(128),
  lastname: Yup.string().max(128),
});

interface RegisterResponse {
  success: boolean;
  data: { uid: number; verifyKey: string };
  error?: { message: string };
}

const RegisterView = () => {
  const [serverError, setServerError] = useState<string | null>(null);
  const navigate = useNavigate();

  return (
    <AuthShell
      title="Create your account"
      subtitle="You'll receive a verification key on the next screen."
      footer={
        <>
          Already have an account?{' '}
          <Link to={ROUTES.LOGIN} className="font-medium text-tfs-primary hover:underline">
            Log in
          </Link>
        </>
      }
    >
      <Formik
        initialValues={{ username: '', email: '', password: '', firstname: '', lastname: '' }}
        validationSchema={RegisterSchema}
        onSubmit={async (values, { setSubmitting }) => {
          setServerError(null);
          try {
            const res = await APIServices.post<RegisterResponse>('/auth/register', values);
            navigate(
              `${ROUTES.VERIFY_ACCOUNT}?membersid=${res.data.uid}&rkey=${encodeURIComponent(
                res.data.verifyKey,
              )}`,
              { replace: true },
            );
          } catch (err) {
            const message =
              (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
                ?.message ?? 'Could not register';
            setServerError(message);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form className="space-y-4">
            <TextField name="username" label="Username" autoComplete="username" />
            <TextField name="email" label="Email" type="email" autoComplete="email" />
            <TextField name="password" label="Password" type="password" autoComplete="new-password" />
            <div className="grid grid-cols-2 gap-3">
              <TextField name="firstname" label="First name" optional />
              <TextField name="lastname" label="Last name" optional />
            </div>

            {serverError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {serverError}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-md bg-tfs-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating account…' : 'Create account'}
            </button>
          </Form>
        )}
      </Formik>
    </AuthShell>
  );
};

interface FieldProps {
  name: string;
  label: string;
  type?: string;
  autoComplete?: string;
  optional?: boolean;
}

const TextField = ({ name, label, type = 'text', autoComplete, optional }: FieldProps) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-slate-700">
      {label}
      {optional && <span className="ml-1 text-xs font-normal text-slate-400">(optional)</span>}
    </label>
    <Field
      id={name}
      name={name}
      type={type}
      autoComplete={autoComplete}
      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-tfs-primary focus:outline-none focus:ring-1 focus:ring-tfs-primary"
    />
    <ErrorMessage name={name} component="p" className="mt-1 text-xs text-red-600" />
  </div>
);

export default RegisterView;
